import "./ProyectosCommon.css";
import { BaseCreateComponent } from "../../../shared/components/BaseCreateComponent";
import { ClienteSearchItem } from "../../../shared/response/ventas/ClienteSearchItem";
import { FincaListItemResponse } from "../../../shared/response/terceros/FincaListItemResponse";
import { FincaListByClienteIdService } from "../../../shared/services/terceros/FincaListByClienteIdService";
import { CultivoResponse } from "../../../shared/response/proyectos/CultivoResponse";
import { CultivoService } from "../../services/proyectos/CultivoService";
import { TecnicoResponse } from "../../../shared/response/proyectos/TecnicoResponse";
import { TecnicoService } from "../../services/proyectos/TecnicoService";
import { SnapshotResponse } from "../../../shared/response/proyectos/SnapshotResponse";
import { SnapshotService } from "../../services/proyectos/SnapshotService";
import { MacroProyectoResponse } from "../../../shared/response/proyectos/MacroProyectoResponse";
import { MacroProyectoService } from "../../services/proyectos/MacroProyectoService";
import { NucleoResponse } from "../../../shared/response/proyectos/NucleoResponse";
import { NucleoService } from "../../services/proyectos/NucleoService";
import { FaseResponse } from "../../../shared/response/proyectos/FaseResponse";
import { FaseService } from "../../services/proyectos/FaseService";
import { CatalogoItemResponse } from "../../../shared/response/proyectos/CatalogoItemResponse";
import { CatalogoService } from "../../services/proyectos/CatalogoService";
import { ItemPropioFormModal } from "./ItemPropioFormModal";
import { ClienteFormModal } from "../terceros/ClienteFormModal";
import { coincideBusqueda, normalizarBusqueda, palabrasBusqueda } from "../../../shared/utils/busquedaTexto";
import { ItemPropioSaveService } from "../../services/proyectos/ItemPropioSaveService";
import { ProyectoItemDetalle, ProyectoResponse } from "../../../shared/response/proyectos/ProyectoResponse";
import { ProyectoService } from "../../services/proyectos/ProyectoService";
import { ProyectoSaveService } from "../../services/proyectos/ProyectoSaveService";
import { ProyectoItemPayload } from "../../../shared/services/api/payload/proyectos/ProyectoPayload";
import { ClienteService, buscarClientes } from "../../services/terceros/ClienteService";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { fmtCentavos, fmtPct } from "../../../shared/utils/proyectosFormat";
import { formatKg } from "../../../shared/utils/formatUtils";
import { categoriaColor } from "../../../shared/utils/categoriaColors";

interface ItemState { activo: boolean; cantidad: number; precio: number }

export class ProyectoWizard extends BaseCreateComponent<HTMLDivElement> {
  private _step: 1 | 2 | 3 | 4 = 1;
  private _cliente: ClienteSearchItem | null = null;
  /** Clientes donde se busca el beneficiario: la lista de `ListClientes`, cargada al abrir el asistente. */
  private _clientes: ClienteResponse[] = [];
  /** Lo escrito en el buscador del beneficiario, para elegir el aviso de la tabla vacía. */
  private _beneTexto = "";
  private _beneResultados: ClienteSearchItem[] = [];
  private _fincas: FincaListItemResponse[] = [];
  private _cultivos: CultivoResponse[] = [];
  private _tecnicos: TecnicoResponse[] = [];
  private _macros: MacroProyectoResponse[] = [];
  private _nucleos: NucleoResponse[] = [];
  private _fases: FaseResponse[] = [];
  private _snapshot: SnapshotResponse | null = null;
  /** Falló la lectura del snapshot activo: el paso 2 lo dice, en vez de «No hay snapshot activo». */
  private _snapshotFallo = false;
  private _created: ProyectoResponse | null = null;
  private _catalogo: CatalogoItemResponse[] = [];
  private _itemState = new Map<string, ItemState>();
  /** Texto del buscador de ítems del Paso 3. Vive acá para sobrevivir a un redibujado de la lista. */
  private _itemBusqueda = "";

  constructor(
    private readonly _onCreated: (p: ProyectoResponse) => void,
    private readonly _editData?: { proyecto: ProyectoResponse; items: ProyectoItemDetalle[] },
  ) { super(); }

  protected getTagName(): string { return "div"; }
  protected getElementId(): string { return "proyectoWizardOverlay"; }
  protected getCssClassList(): string[] { return ["pr-wz-overlay"]; }

  protected override afterInit(el: HTMLDivElement): void {
    el.innerHTML = `<div class="pr-wz-card"><div class="pr-wz-body" id="wz-body"><div class="pr-empty">Cargando…</div></div></div>`;
    el.addEventListener("click", (e) => { if (e.target === el) this.dispose(); });
    if (this._editData) void this._initEdit();
    else void this._loadCatalogos();
  }

  /** Modo edición: reabre el Paso 2 con los ítems actuales del proyecto (corregir precios/cantidades). */
  private async _initEdit(): Promise<void> {
    const proyecto = this._editData!.proyecto;
    this._created = proyecto;
    // Con los ítems propios del beneficiario, y esto NO es opcional: `_crear()` manda `_catalogo`
    // entero y el servidor borra del proyecto todo ítem que no venga en el envío. Si la lista no
    // trajera los propios, reabrir el paquete para corregir una cantidad le borraría al beneficiario
    // sus ítems propios -y con ellos su plata del presupuesto- sin avisar.
    const [catalogo, fases] = await Promise.all([
      CatalogoService.instance.getByCultivoIdConPropios(proyecto.cultivoId, proyecto.clienteId),
      FaseService.instance.get().catch(() => [] as FaseResponse[]),
    ]);
    this._catalogo = catalogo
      .sort((a, b) => (a.sec || "").localeCompare(b.sec || "") || (a.sub || "").localeCompare(b.sub || "") || a.nombre.localeCompare(b.nombre));
    this._fases = fases.filter((f) => !f.isEliminado).sort((a, b) => a.orden - b.orden);
    const jornal = proyecto.snapshotFactorJornal ?? 0;
    const byItemId = new Map(this._editData!.items.map((i) => [i.itemId, i]));
    this._itemState.clear();
    this._catalogo.forEach((it) => {
      const pi = byItemId.get(it.id);
      this._itemState.set(it.id, {
        // Los ítems obligatorios (candado) siempre quedan activos, sin importar lo guardado antes.
        activo: it.incl === "obligatorio" ? true : (pi ? pi.activo : it.activo),
        cantidad: pi ? pi.cantidad : it.cantidadPorHa,
        precio: it.tipoP === "smmlv" ? jornal : (pi?.precioUnitario ?? it.precioVigente ?? 0),
      });
    });
    this._step = 3;
    this._renderStep3();
  }

  /**
   * Carga lo que piden los pasos de un proyecto nuevo: cultivos, técnicos, parámetros, macro-proyectos, núcleos, fases y
   * la lista de clientes donde se busca el beneficiario (`ClienteService.get` nunca rechaza: si falla, avisa y deja la
   * última lista cargada). Si falla la lectura del snapshot activo, muestra el error, lo escribe en consola y lo marca
   * (`_snapshotFallo`), para que el paso 2 diga que no se pudo leer y no «No hay snapshot activo», que sería falso
   * (revisiones del 2026-10-29; antes quedaba en silencio). Después pinta el paso 1.
   */
  private async _loadCatalogos(): Promise<void> {
    const [cultivos, tecnicos, snapshot, macros, nucleos, fases, clientes] = await Promise.all([
      CultivoService.instance.get().catch(() => [] as CultivoResponse[]),
      TecnicoService.instance.get().catch(() => [] as TecnicoResponse[]),
      SnapshotService.instance.getActivo().catch((error) => {
        console.warn("[ProyectoWizard._loadCatalogos] no se pudo leer el snapshot activo", error);
        errorMessageComponent.addError("No se pudo leer el snapshot activo", error instanceof Error ? error.message : "Error desconocido.");
        this._snapshotFallo = true;
        return null;
      }),
      MacroProyectoService.instance.get().catch(() => [] as MacroProyectoResponse[]),
      NucleoService.instance.get().catch(() => [] as NucleoResponse[]),
      FaseService.instance.get().catch(() => [] as FaseResponse[]),
      ClienteService.instance.get(),
    ]);
    this._clientes = clientes;
    this._cultivos = cultivos.filter((c) => !c.isEliminado).sort((a, b) => a.nombre.localeCompare(b.nombre));
    this._tecnicos = tecnicos.filter((t) => !t.isEliminado && t.isActivo).sort((a, b) => a.nombre.localeCompare(b.nombre));
    this._snapshot = snapshot;
    this._macros = macros.filter((m) => !m.isEliminado && m.estado !== "cerrado").sort((a, b) => a.nombre.localeCompare(b.nombre));
    this._nucleos = nucleos.filter((n) => !n.isEliminado).sort((a, b) => a.nombre.localeCompare(b.nombre));
    this._fases = fases.filter((f) => !f.isEliminado).sort((a, b) => a.orden - b.orden);
    this._renderStep1();
  }

  private get _body(): HTMLElement { return this.getElement().querySelector("#wz-body")!; }

  private _stepperHtml(): string {
    // En modo edición no hay paso 1/2 (nunca se visitan) ni paso 4 (corregir el paquete no
    // necesita confirmación aparte): solo tiene sentido mostrar el paso del paquete.
    if (this._editData) {
      return `<div class="pr-wz-stepper"><div class="pr-wz-step active"><span class="pr-wz-circle">•</span> Paquete tecnológico</div></div>`;
    }
    return `
      <div class="pr-wz-stepper">
        <div class="pr-wz-step ${this._step === 1 ? "active" : ""}"><span class="pr-wz-circle">${this._step > 1 ? "✓" : "1"}</span> Beneficiario</div>
        <div class="pr-wz-line ${this._step > 1 ? "done" : ""}"></div>
        <div class="pr-wz-step ${this._step === 2 ? "active" : ""}"><span class="pr-wz-circle">${this._step > 2 ? "✓" : "2"}</span> Configuración</div>
        <div class="pr-wz-line ${this._step > 2 ? "done" : ""}"></div>
        <div class="pr-wz-step ${this._step === 3 ? "active" : ""}"><span class="pr-wz-circle">${this._step > 3 ? "✓" : "3"}</span> Paquete tecnológico</div>
        <div class="pr-wz-line ${this._step > 3 ? "done" : ""}"></div>
        <div class="pr-wz-step ${this._step === 4 ? "active" : ""}"><span class="pr-wz-circle">4</span> Confirmar</div>
      </div>`;
  }

  // ── PASO 1: BENEFICIARIO ───────────────────────────────────────────────────
  private _renderStep1(): void {
    this._body.innerHTML = `
      <div class="pr-wz-hd">
        <div class="pr-wz-title">Nuevo proyecto</div>
        <button class="pr-wz-close" id="wz-close">✕</button>
      </div>
      ${this._stepperHtml()}
      <div class="pr-wz-lbl">Beneficiario <span class="cl-required">*</span></div>
      <div class="pr-wz-search" style="display:flex;align-items:center;gap:10px;margin-bottom:12px">
        <input class="sx-input" id="wz-cli" type="search" autocomplete="off" style="flex:1" placeholder="Buscar por nombre o NIT…" value="${this._cliente ? this._esc(this._cliente.nombre) : ""}">
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="wz-nuevo-cli" type="button">
          <i class="ph ph-user-plus"></i> Crear beneficiario
        </button>
      </div>
      <div class="pr-tbl-wrap">
        <table class="pr-tbl">
          <thead><tr><th></th><th>Beneficiario</th><th>Ubicación</th><th>Núcleo asignado</th><th>Proyectos activos</th></tr></thead>
          <tbody id="wz-bene-tbody">${this._beneRowsHtml()}</tbody>
        </table>
      </div>

      <div class="pr-wz-footer">
        <button class="sx-btn sx-btn-ghost" id="wz-cancel">Cancelar</button>
        <button class="sx-btn sx-btn-primary" id="wz-next" ${this._cliente ? "" : "disabled"}>Continuar → Configuración</button>
      </div>
    `;

    this.getElement().querySelector<HTMLButtonElement>("#wz-close")!.onclick = () => this.dispose();
    this.getElement().querySelector<HTMLButtonElement>("#wz-cancel")!.onclick = () => this.dispose();
    const cliInput = this._body.querySelector<HTMLInputElement>("#wz-cli")!;
    cliInput.addEventListener("input", () => this._onSearch(cliInput.value));
    this._body.querySelector<HTMLButtonElement>("#wz-nuevo-cli")!.onclick = () => this._crearCliente();
    this._wireBeneRows();
    this._body.querySelector<HTMLButtonElement>("#wz-next")!.onclick = () => this._goStep2();
  }

  /**
   * Filas de la tabla de beneficiarios: cada cliente encontrado con su ubicación, núcleo y proyectos activos (los trae
   * `ListClientes`), y el elegido marcado. Sin nada escrito, pide escribir. Escrito y sin coincidencias, ofrece crearlo
   * ahí mismo con lo escrito: salir del asistente, ir a Clientes, crearlo y volver a empezar el proyecto es lo que se
   * quería evitar. El botón «Crear beneficiario» de arriba se ve siempre (pedido del usuario, 2026-10-31).
   */
  private _beneRowsHtml(): string {
    if (this._beneResultados.length === 0) {
      const escrito = this._beneTexto.trim();
      if (escrito === "") {
        return `<tr><td colspan="5" class="pr-empty">Escribe el nombre o el NIT del beneficiario.</td></tr>`;
      }
      return `<tr><td colspan="5" class="pr-empty">
             No hay ningún beneficiario que coincida con "${this._esc(escrito)}".
             <button class="sx-btn sx-btn-secondary sx-btn-sm" id="wz-nuevo-cli-vacio" type="button" style="margin-left:10px">
               <i class="ph ph-user-plus"></i> Crear "${this._esc(escrito)}"
             </button>
           </td></tr>`;
    }
    return this._beneResultados.map((c) => `
      <tr class="pr-wz-bene-row${this._cliente?.id === c.id ? " selected" : ""}" data-id="${c.id}" style="cursor:pointer">
        <td><span class="pr-wz-radio"></span></td>
        <td><div style="font-weight:var(--sx-weight-semibold)">${this._esc(c.nombre)}</div><div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">NIT ${this._esc(c.nit)}</div></td>
        <td>${c.ubicacion ? this._esc(c.ubicacion) : "—"}</td>
        <td>${c.nucleoAsignado ? this._esc(c.nucleoAsignado) : "—"}</td>
        <td class="pr-num">${c.proyectosActivos > 0 ? c.proyectosActivos : "—"}</td>
      </tr>`).join("");
  }

  /** Conecta las filas de beneficiarios (elegir) y el botón «Crear …» de la tabla vacía; se llama en cada redibujado. */
  private _wireBeneRows(): void {
    this._body.querySelectorAll<HTMLElement>(".pr-wz-bene-row").forEach((row) => {
      row.onclick = () => void this._selectCliente(this._beneResultados.find((c) => c.id === row.dataset.id)!);
    });
    // El botón del estado vacío se redibuja con el tbody, así que se vuelve a cablear acá.
    const crear = this._body.querySelector<HTMLButtonElement>("#wz-nuevo-cli-vacio");
    if (crear) crear.onclick = () => this._crearCliente();
  }

  /**
   * Abre el alta de beneficiario con lo que ya se escribió en el buscador. **Empieza con dígito → NIT, si
   * no → nombre**: la misma regla que el wizard de Ventas (`_modalPrefill`), para que no haya dos criterios
   * distintos en la misma app. Adivinar mal cuesta borrar un campo; no adivinar cuesta escribirlo dos veces.
   */
  private _crearCliente(): void {
    const escrito = this._body.querySelector<HTMLInputElement>("#wz-cli")!.value.trim();
    const pareceNit = /^\d/.test(escrito);
    const prefill = escrito.length === 0 ? undefined : pareceNit ? { nit: escrito } : { nombre: escrito };
    new ClienteFormModal((creado) => void this._onClienteCreado(creado), undefined, prefill).init();
  }

  /**
   * Deja seleccionado el beneficiario recién creado y sigue el wizard. La fila se arma acá en vez de volver
   * a buscar: un cliente nuevo no tiene fincas, ni núcleo, ni proyectos, así que esos campos son 0 o null
   * con certeza. También se suma a la lista donde busca el asistente, que se cargó al abrirlo: si no, al
   * volver a escribir en el buscador no aparecería.
   */
  private async _onClienteCreado(creado: ClienteResponse): Promise<void> {
    this._clientes.push(creado);
    const item: ClienteSearchItem = {
      id: creado.id,
      nombre: creado.nombre,
      nit: creado.nit,
      telefono: creado.telefono,
      hectareasTotales: 0,
      ubicacion: null,
      nucleoAsignado: null,
      proyectosActivos: 0,
    };
    this._beneResultados = [item];
    this._beneTexto = creado.nombre;
    const input = this._body.querySelector<HTMLInputElement>("#wz-cli");
    if (input) input.value = creado.nombre;
    await this._selectCliente(item);
  }

  /**
   * Busca el beneficiario en la lista de clientes que el asistente cargó al abrirse (`buscarClientes`, en el navegador):
   * al momento, sin mínimo de letras ni esperas, hasta 10. Antes del 2026-10-28 le preguntaba al servidor
   * (`BuscarClientes`) desde 2 letras.
   */
  private _onSearch(q: string): void {
    this._beneTexto = q;
    this._beneResultados = buscarClientes(this._clientes, q, 10);
    this._body.querySelector<HTMLElement>("#wz-bene-tbody")!.innerHTML = this._beneRowsHtml();
    this._wireBeneRows();
  }

  /**
   * Elige el beneficiario del proyecto y carga sus fincas activas para el paso 2. Si la carga falla, avisa y escribe
   * el error en consola: sin eso se vería como un beneficiario sin fincas, y la finca es obligatoria desde el
   * 2026-10-24.
   */
  private async _selectCliente(c: ClienteSearchItem): Promise<void> {
    this._cliente = c;
    try { this._fincas = (await FincaListByClienteIdService.instance(c.id).get()).filter((f) => !f.isEliminado); }
    catch (error) {
      console.warn(`[ProyectoWizard._selectCliente] no se pudieron cargar las fincas del cliente ${c.id}`, error);
      errorMessageComponent.addError("Error al cargar las fincas", (error as Error).message);
      this._fincas = [];
    }
    this._body.querySelector<HTMLElement>("#wz-bene-tbody")!.innerHTML = this._beneRowsHtml();
    this._wireBeneRows();
    this._body.querySelector<HTMLButtonElement>("#wz-next")!.disabled = false;
  }

  private _goStep2(): void {
    if (!this._cliente) return;
    this._step = 2;
    this._renderStep2();
  }

  // ── PASO 2: CONFIGURACIÓN ──────────────────────────────────────────────────
  /**
   * Pinta el paso 2, la configuración del proyecto, y conecta sus controles. Sin snapshot activo avisa y no deja
   * seguir. Si lo que pasó es que falló su lectura (`_snapshotFallo`), lo dice y pide reabrir el asistente, porque este
   * paso no la vuelve a pedir (revisión del 2026-10-29).
   */
  private _renderStep2(): void {
    const noSnap = this._snapshot == null;
    const avisoSnap = this._snapshotFallo
      ? `No se pudo leer el snapshot macroeconómico activo. Cierra el asistente y vuelve a abrirlo.`
      : `No hay snapshot macroeconómico activo. Activa uno en <strong>Macroeconómico</strong> antes de crear un proyecto.`;
    this._body.innerHTML = `
      <div class="pr-wz-hd">
        <div class="pr-wz-title">Nuevo proyecto</div>
        <button class="pr-wz-close" id="wz-close">✕</button>
      </div>
      ${this._stepperHtml()}
      ${noSnap ? `<div class="pr-wz-warn">${avisoSnap}</div>` : ""}

      <div class="pr-wz-card2">
        <div class="pr-form-row">
          <div><div class="pr-wz-lbl">Macro-Proyecto</div>
            <select class="sx-input" id="wz-macro">
              <option value="">— Ninguno —</option>
              ${this._macros.map((m) => `<option value="${m.id}">${this._esc(m.nombre)}</option>`).join("")}
            </select>
          </div>
          <div><div class="pr-wz-lbl">Núcleo</div>
            <select class="sx-input" id="wz-nucleo">
              <option value="">— Hereda del último proyecto del cliente —</option>
              ${this._nucleos.map((n) => `<option value="${n.id}">${this._esc(n.nombre)}</option>`).join("")}
            </select>
          </div>
        </div>
        <div id="wz-nucleo-adv" hidden style="margin-top:12px"></div>
      </div>

      <div class="pr-wz-card2" id="wz-finca-card">
        <div class="pr-form-row">
          <div><div class="pr-wz-lbl">Finca <span class="cl-required">*</span></div>
            <select class="sx-input" id="wz-finca"><option value="">— Selecciona finca —</option></select>
          </div>
          <div><div class="pr-wz-lbl">Cultivo <span class="cl-required">*</span></div>
            <select class="sx-input" id="wz-cultivo">
              <option value="">— Selecciona cultivo —</option>
              ${this._cultivos.map((c) => `<option value="${c.id}">${this._esc(c.nombre)} · ${c.mesesACosecha}m</option>`).join("")}
            </select>
          </div>
        </div>
        <div class="pr-form-row" style="margin-top:12px">
          <div><div class="pr-wz-lbl">Hectáreas <span class="cl-required">*</span></div>
            <input class="sx-input sx-input--numeric" id="wz-ha" type="number" min="0" step="0.1" placeholder="0"></div>
          <div><div class="pr-wz-lbl">Variedad</div>
            <input class="sx-input" id="wz-variedad" type="text" placeholder="Ej: Dominico Hartón"></div>
        </div>
        <div class="pr-form-row" style="margin-top:12px">
          <div><div class="pr-wz-lbl">Municipio</div>
            <input class="sx-input" id="wz-muni" type="text" placeholder="Municipio"></div>
          <div><div class="pr-wz-lbl">Fecha de inicio</div>
            <input class="sx-input" id="wz-fecha" type="date"></div>
        </div>
      </div>

      <div class="pr-wz-card2" id="wz-proy-card">
        <div class="pr-form-row">
          <div><div class="pr-wz-lbl">Nombre del proyecto <span class="cl-required">*</span></div>
            <input class="sx-input" id="wz-nombre" type="text" placeholder="Ej: Finca La Gitana — Plátano 2026"></div>
          <div><div class="pr-wz-lbl">Técnico <span class="cl-required">*</span></div>
            <select class="sx-input" id="wz-tecnico"><option value="">— Selecciona técnico —</option>
              ${this._tecnicos.map((t) => `<option value="${t.id}">${this._esc(t.nombre)}</option>`).join("")}
            </select></div>
        </div>
        ${this._snapshot ? `<div class="pr-form-note" style="margin-top:10px">Snapshot vigente: <strong>${this._snapshot.año}</strong> · IPC ${fmtPct(this._snapshot.ipc)} · Jornal ${fmtCentavos(this._snapshot.factorJornal)} (se congela al crear)</div>` : ""}
      </div>

      <div class="pr-wz-card2">
        <div class="pr-wz-lbl">Crédito de referencia (opcional)</div>
        <div class="pr-form-row" style="margin-top:8px">
          <div><div class="pr-wz-lbl">Entidad financiera</div>
            <input class="sx-input" id="wz-entidad" type="text" placeholder="Ej: Banco Agrario, Finagro…"></div>
          <div><div class="pr-wz-lbl">Límite financiero (COP)</div>
            <input class="sx-input sx-input--numeric" id="wz-limite" type="number" min="0" step="1" placeholder="Ej: 80000000"></div>
        </div>
        <span class="pr-form-note">Solo referencial — el sistema avisa si el presupuesto lo supera, no bloquea la creación.</span>
        <div class="pr-form-row" style="margin-top:12px">
          <div><div class="pr-wz-lbl">Duración estimada</div>
            <select class="sx-input" id="wz-duracion">
              <option value="">— Sin definir —</option>
              <option value="12">12 meses</option>
              <option value="18">18 meses</option>
              <option value="24">24 meses</option>
              <option value="36">36 meses</option>
            </select>
          </div>
          <div></div>
        </div>
      </div>

      <div class="pr-wz-footer pr-wz-footer-nav">
        <button class="sx-btn sx-btn-ghost" id="wz-back2">← Anterior</button>
        <button class="sx-btn sx-btn-primary" id="wz-next" ${noSnap ? "disabled" : ""}>Continuar → Paquete</button>
      </div>
    `;

    this.getElement().querySelector<HTMLButtonElement>("#wz-close")!.onclick = () => this.dispose();
    this.getElement().querySelector<HTMLButtonElement>("#wz-back2")!.onclick = () => { this._step = 1; this._renderStep1(); };
    this._populateFincas();
    this._body.querySelector<HTMLSelectElement>("#wz-finca")?.addEventListener("change", (e) => this._onFinca((e.target as HTMLSelectElement).value));
    this._body.querySelector<HTMLSelectElement>("#wz-nucleo")!.addEventListener("change", () => this._onNucleoChange());
    this._body.querySelector<HTMLInputElement>("#wz-ha")!.addEventListener("input", () => this._updateNucleoAdvisory());
    this._body.querySelector<HTMLButtonElement>("#wz-next")!.onclick = () => void this._goStep3();
  }

  // Al elegir núcleo, sugiere su monto máx. referencial como límite financiero -si el usuario no
  // ha escrito ya uno a mano- y recalcula la advertencia de Ha máx. de asociación.
  private _onNucleoChange(): void {
    const n = this._nucleos.find((x) => x.id === this._body.querySelector<HTMLSelectElement>("#wz-nucleo")!.value);
    const limiteInput = this._body.querySelector<HTMLInputElement>("#wz-limite")!;
    if (n?.montoMaxReferencial != null && !limiteInput.value) limiteInput.value = String(n.montoMaxReferencial);
    this._updateNucleoAdvisory();
  }

  private _updateNucleoAdvisory(): void {
    const box = this._body.querySelector<HTMLElement>("#wz-nucleo-adv");
    if (!box) return;
    const n = this._nucleos.find((x) => x.id === this._body.querySelector<HTMLSelectElement>("#wz-nucleo")!.value);
    if (!n || (n.haMaxAsociacion == null && n.montoMaxReferencial == null)) { box.hidden = true; return; }
    const ha = Number(this._body.querySelector<HTMLInputElement>("#wz-ha")!.value) || 0;
    const superaHa = n.haMaxAsociacion != null && ha > n.haMaxAsociacion;
    box.hidden = false;
    box.className = "pr-alert-warn";
    box.style.background = superaHa ? "" : "var(--sx-success-bg)";
    box.style.borderColor = superaHa ? "" : "var(--sx-success-border)";
    box.style.color = superaHa ? "" : "var(--sx-success)";
    const partes: string[] = [];
    if (n.haMaxAsociacion != null) partes.push(`Ha máx. de asociación <strong>${formatKg(n.haMaxAsociacion)}</strong>`);
    if (n.montoMaxReferencial != null) partes.push(`Monto máx. referencial <strong>${fmtCentavos(n.montoMaxReferencial)}</strong>`);
    box.innerHTML = `<strong>Núcleo "${this._esc(n.nombre)}"</strong><div style="margin-top:3px">${partes.join(" · ")}</div>` +
      (superaHa ? `<div style="margin-top:3px">Las ${formatKg(ha)} ha de este proyecto superan el límite de asociación del núcleo.</div>` : "");
  }

  /**
   * Llena el selector de finca con las fincas activas del beneficiario. La finca es obligatoria desde el 2026-10-24
   * (pedido del usuario: "no se debería poder tener un proyecto sin finca asignada"): si el beneficiario no tiene
   * ninguna, el selector lo dice para que se registre una antes de crear el proyecto.
   */
  private _populateFincas(): void {
    const sel = this._body.querySelector<HTMLSelectElement>("#wz-finca");
    const vacio = this._fincas.length === 0 ? "— El beneficiario no tiene fincas registradas —" : "— Selecciona finca —";
    if (sel) sel.innerHTML = `<option value="">${vacio}</option>` +
      this._fincas.map((f) => `<option value="${f.id}">${this._esc(f.nombreFinca)}${f.municipioNombre ? " · " + this._esc(f.municipioNombre) : ""}</option>`).join("");
  }

  private _onFinca(fincaId: string): void {
    const f = this._fincas.find((x) => x.id === fincaId);
    if (!f) return;
    (this._body.querySelector<HTMLInputElement>("#wz-ha")!).value = String(f.hectareasFinca ?? "");
    (this._body.querySelector<HTMLInputElement>("#wz-muni")!).value = f.municipioNombre ?? "";
  }

  /**
   * Valida el paso 2 y crea el proyecto con `GuardarProyecto`. Marca en rojo lo que falta; la finca es obligatoria
   * desde el 2026-10-24 (el Server también la exige). Con el proyecto creado, carga el catálogo del cultivo y pasa al
   * paso 3.
   */
  private async _goStep3(): Promise<void> {
    if (!this._cliente || !this._snapshot) return;
    const cultivoId = this._body.querySelector<HTMLSelectElement>("#wz-cultivo")!.value;
    const ha = Number(this._body.querySelector<HTMLInputElement>("#wz-ha")!.value) || 0;
    const nombre = this._body.querySelector<HTMLInputElement>("#wz-nombre")!.value.trim();
    const tecnicoId = this._body.querySelector<HTMLSelectElement>("#wz-tecnico")!.value;
    const fincaId = this._body.querySelector<HTMLSelectElement>("#wz-finca")!.value || null;
    const variedad = this._body.querySelector<HTMLInputElement>("#wz-variedad")!.value.trim() || null;
    const municipio = this._body.querySelector<HTMLInputElement>("#wz-muni")!.value.trim() || null;
    const fechaRaw = this._body.querySelector<HTMLInputElement>("#wz-fecha")!.value;
    const macroProyectoId = this._body.querySelector<HTMLSelectElement>("#wz-macro")!.value || null;
    const nucleoId = this._body.querySelector<HTMLSelectElement>("#wz-nucleo")!.value || null;
    const entidadFinancieraReferencia = this._body.querySelector<HTMLInputElement>("#wz-entidad")!.value.trim() || null;
    const limiteRaw = this._body.querySelector<HTMLInputElement>("#wz-limite")!.value;
    const limiteFinancieroReferencial = limiteRaw ? Math.floor(Number(limiteRaw)) : null;
    const duracionRaw = this._body.querySelector<HTMLSelectElement>("#wz-duracion")!.value;
    const duracionMeses = duracionRaw ? Number(duracionRaw) : null;

    // Avisa exactamente qué falta (el cliente ya viene del paso 1) y marca esos campos en rojo, con el foco
    // en el primero; la marca se quita sola al corregir el campo.
    const faltan: [string, string][] = [];
    if (!fincaId) {
      faltan.push(["#wz-finca", this._fincas.length === 0
        ? "finca (el beneficiario no tiene fincas: registra una antes de crear el proyecto)"
        : "finca"]);
    }
    if (!cultivoId) faltan.push(["#wz-cultivo", "cultivo"]);
    if (ha <= 0) faltan.push(["#wz-ha", "hectáreas (mayor que 0)"]);
    if (!nombre) faltan.push(["#wz-nombre", "nombre del proyecto"]);
    if (!tecnicoId) faltan.push(["#wz-tecnico", "técnico"]);
    if (faltan.length > 0) {
      for (const [selector] of faltan) {
        const campo = this._body.querySelector<HTMLInputElement | HTMLSelectElement>(selector);
        if (!campo) continue;
        campo.classList.add("error");
        const limpiar = () => campo.classList.remove("error");
        campo.addEventListener("input", limpiar, { once: true });
        campo.addEventListener("change", limpiar, { once: true });
      }
      this._body.querySelector<HTMLElement>(faltan[0][0])?.focus();
      errorMessageComponent.addWarning("Faltan datos", `Completa: ${faltan.map(([, nombreCampo]) => nombreCampo).join(", ")}.`);
      return;
    }

    const btn = this._body.querySelector<HTMLButtonElement>("#wz-next")!;
    btn.disabled = true; btn.textContent = "Creando…";
    try {
      this._created = await new ProyectoSaveService().save({
        id: null, nombre, clienteId: this._cliente.id, cultivoId, tecnicoId, ha,
        fincaId, variedad, municipio, fechaInicio: fechaRaw ? new Date(fechaRaw).toISOString() : null, nota: null,
        // nucleoId=null → el backend hereda el del último proyecto del cliente.
        macroProyectoId, nucleoId, duracionMeses,
        entidadFinancieraReferencia, limiteFinancieroReferencial,
      });
      // Con los ítems propios que el beneficiario ya tenga de otro proyecto del mismo cultivo: son suyos,
      // así que reaparecen -apagados o prendidos según su `activo`- en vez de tener que volver a crearlos.
      this._catalogo = (await CatalogoService.instance.getByCultivoIdConPropios(cultivoId, this._cliente.id))
        .sort((a, b) => (a.sec || "").localeCompare(b.sec || "") || (a.sub || "").localeCompare(b.sub || "") || a.nombre.localeCompare(b.nombre));
      this._itemState.clear();
      const jornal = this._snapshot?.factorJornal ?? 0;
      // smmlv → precio automático (jornal del snapshot); resto → precio vigente del catálogo (editable).
      // Los ítems obligatorios (candado) siempre arrancan activos.
      this._catalogo.forEach((it) => this._itemState.set(it.id, {
        activo: it.incl === "obligatorio" ? true : it.activo,
        cantidad: it.cantidadPorHa,
        precio: it.tipoP === "smmlv" ? jornal : (it.precioVigente ?? 0),
      }));
      this._step = 3;
      this._renderStep3();
    } catch (error) {
      errorMessageComponent.addError("Error al crear el proyecto", (error as Error).message);
      btn.disabled = false; btn.textContent = "Continuar → Paquete";
    }
  }

  // ── PASO 3: PAQUETE TECNOLÓGICO ─────────────────────────────────────────────
  private _renderStep3(): void {
    const ha = this._created!.ha;
    this._body.innerHTML = `
      <div class="pr-wz-hd">
        <div class="pr-wz-title">${this._esc(this._created!.nombre)}</div>
        <button class="pr-wz-close" id="wz-close">✕</button>
      </div>
      ${this._stepperHtml()}
      ${this._ctxStripHtml()}
      <div class="pr-form-note" style="margin:0 0 8px">Activa los ítems que aplican y ajusta la cantidad por Ha. El presupuesto se recalcula automáticamente.</div>
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
        <input id="wz-search" class="sx-input pr-budget-search" type="search" autocomplete="off"
               placeholder="Buscar ítem, categoría o subsección…" value="${this._esc(this._itemBusqueda)}">
        <span id="wz-item-count" class="pr-flbl"></span>
        <div style="flex:1"></div>
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="wz-add-propio" type="button"
                ${this._fases.length === 0 ? `disabled title="No se pudieron cargar las fases; sin fase no se puede crear el ítem"` : ""}>
          <i class="ph ph-plus"></i> Ítem propio del beneficiario
        </button>
      </div>
      <div class="pr-wz-body2">
        <div class="pr-wz-items" id="wz-items"></div>
        <div class="pr-wz-pres">
          <div class="pr-panel">
            <div class="pr-panel-t">Presupuesto</div>
            <div class="pr-wz-pres-row"><span>Área</span><span>${ha} Ha</span></div>
            <div class="pr-wz-pres-row"><span>CAPEX</span><span id="wz-capex" class="pr-num">$0</span></div>
            <div class="pr-wz-pres-row"><span>OPEX anual</span><span id="wz-opex" class="pr-num">$0</span></div>
            <div class="pr-wz-pres-row" id="wz-propios-row" hidden><span>De los cuales, ítems propios</span><span id="wz-propios" class="pr-num">$0</span></div>
            <div class="pr-wz-pres-total"><span>Total</span><span id="wz-total" class="pr-num">$0</span></div>
            <div class="pr-wz-pres-count" id="wz-count"></div>
            <div class="pr-wz-pres-warn" id="wz-sinprecio" hidden></div>
            <div id="wz-limite" hidden style="margin-top:12px">
              <div class="pr-bar-row" style="margin-bottom:5px">
                <span class="pr-bar-lbl" style="width:auto;flex:1">vs. límite referencial</span>
                <span class="pr-bar-val" id="wz-limite-pct" style="width:auto"></span>
              </div>
              <div class="pr-bar-track"><div class="pr-bar-fill" id="wz-limite-fill" style="width:0%"></div></div>
              <div id="wz-limite-cap" style="font-size:var(--sx-text-xs);margin-top:5px"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="pr-wz-footer">
        <button class="sx-btn sx-btn-ghost" id="wz-crear">${this._siguienteTexto()}</button>
        <button class="sx-btn sx-btn-primary" id="wz-crear2">${this._siguienteTexto()}</button>
      </div>
    `;
    this.getElement().querySelector<HTMLButtonElement>("#wz-close")!.onclick = () => this.dispose();
    this.getElement().querySelector<HTMLButtonElement>("#wz-add-propio")!.onclick = () => this._abrirItemPropio();
    const buscador = this._body.querySelector<HTMLInputElement>("#wz-search")!;
    buscador.addEventListener("input", () => { this._itemBusqueda = buscador.value; this._filtrarItems(); });
    this._renderItems();
    const irSiguiente = () => { if (this._editData) void this._crear(); else this._goStep4(); };
    this.getElement().querySelector<HTMLButtonElement>("#wz-crear")!.onclick = irSiguiente;
    this.getElement().querySelector<HTMLButtonElement>("#wz-crear2")!.onclick = irSiguiente;
    this._recalc();
  }


  // ── Ítems propios del beneficiario ──────────────────────────────────────────

  /** Factor jornal vigente: del snapshot activo al crear, o el congelado del proyecto al editar. */
  private get _jornal(): number {
    return this._snapshot?.factorJornal ?? this._created?.snapshotFactorJornal ?? 0;
  }

  /** Ordena el catálogo del paso 3 por sección, subsección y nombre, el mismo orden con que se cargó. */
  private _ordenarCatalogo(): void {
    this._catalogo.sort((a, b) =>
      (a.sec || "").localeCompare(b.sec || "") || (a.sub || "").localeCompare(b.sub || "") || a.nombre.localeCompare(b.nombre));
  }

  /**
   * Abre el alta de un ítem que este beneficiario necesita y que no está en el catálogo del cultivo.
   * Las categorías y subsecciones del catálogo cargado se pasan como sugerencias, para que el ítem
   * propio caiga en los mismos grupos que el resto y no invente una sección nueva por cada uno.
   */
  private _abrirItemPropio(): void {
    const p = this._created!;
    const categorias = [...new Set(this._catalogo.map((i) => i.categoria).filter(Boolean))].sort();
    // Subsecciones por fase y de más usada a menos: el modal prellena la primera para que el ítem propio
    // caiga en un grupo que ya existe en vez de abrir una sección nueva por cada uno.
    const subsPorFase = new Map<string, string[]>();
    for (const fase of this._fases) {
      const conteo = new Map<string, number>();
      for (const it of this._catalogo) {
        if (it.faseId !== fase.id || !it.sub) continue;
        conteo.set(it.sub, (conteo.get(it.sub) ?? 0) + 1);
      }
      subsPorFase.set(fase.id, [...conteo.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([sub]) => sub));
    }
    new ItemPropioFormModal(p.cultivoId, p.clienteId, p.clienteNombre, this._fases, categorias, subsPorFase, (item) => {
      // El guardado es idempotente: si se reusó un ítem que el beneficiario ya tenía, vuelve el mismo id.
      const indice = this._catalogo.findIndex((x) => x.id === item.id);
      if (indice >= 0) this._catalogo[indice] = item;
      else this._catalogo.push(item);
      this._ordenarCatalogo();
      if (!this._itemState.has(item.id)) {
        this._itemState.set(item.id, {
          activo: true,
          cantidad: item.cantidadPorHa,
          precio: item.tipoP === "smmlv" ? this._jornal : (item.precioVigente ?? 0),
        });
      }
      // La lista cacheada de este beneficiario quedó vieja; que la próxima vez se pida de nuevo.
      CatalogoService.instance.invalidarConPropios(p.cultivoId, p.clienteId);
      this._renderStep3();
    }).init();
  }

  /**
   * Suelta un ítem propio: deja de ser de este beneficiario y sale del paquete. No toca el ítem, que
   * puede tener otros dueños. Solo se ofrece para ítems propios; el catálogo maestro no se toca desde acá.
   */
  private async _quitarItemPropio(item: CatalogoItemResponse): Promise<void> {
    const p = this._created!;
    try {
      await new ItemPropioSaveService().quitar(item.id, p.clienteId);
      this._catalogo = this._catalogo.filter((x) => x.id !== item.id);
      this._itemState.delete(item.id);
      CatalogoService.instance.invalidarConPropios(p.cultivoId, p.clienteId);
      this._renderStep3();
    } catch (error) {
      errorMessageComponent.addError("No se pudo quitar el ítem", (error as Error).message);
    }
  }

  private _siguienteTexto(): string {
    return this._editData ? "Guardar paquete" : "Continuar → Confirmar";
  }

  /** Ítems agrupados por fase (F0/F1/F2, en orden) + un grupo final para los que no tienen fase asignada. */
  private _faseGroups(): { key: string; fase: FaseResponse | null; items: CatalogoItemResponse[] }[] {
    const groups: { key: string; fase: FaseResponse | null; items: CatalogoItemResponse[] }[] = this._fases
      .map((fase) => ({ key: fase.codigo, fase, items: this._catalogo.filter((it) => it.faseCodigo === fase.codigo) }))
      .filter((g) => g.items.length > 0);
    const sinFase = this._catalogo.filter((it) => !it.faseCodigo);
    if (sinFase.length > 0) groups.push({ key: "SINFASE", fase: null, items: sinFase });
    return groups;
  }

  private _ipcMultiplicador(fase: FaseResponse): number {
    const ipc = this._snapshot?.ipc ?? 0;
    return Math.pow(1 + ipc, Math.floor(fase.mesInicio / 12));
  }

  private _renderItems(): void {
    const cont = this._body.querySelector<HTMLElement>("#wz-items")!;
    const groups = this._faseGroups();
    if (groups.length === 0) {
      cont.innerHTML = `<div class="pr-empty">Este cultivo no tiene ítems en el catálogo.</div>`;
      return;
    }

    const html: string[] = [];
    for (const { key, fase, items } of groups) {
      const mult = fase ? this._ipcMultiplicador(fase) : 1;
      html.push(`
        <div class="pr-wz-fase" id="wzf-${key}">
          <div class="pr-wz-fase-hd" data-fase-toggle="${key}">
            <svg class="pr-wz-fase-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
            ${fase ? `<span class="pr-tipo-badge ${fase.tipo === "CAPEX" ? "capex" : "opex"}">${fase.tipo}</span>` : ""}
            <div class="pr-wz-fase-info">
              <div class="pr-wz-fase-name">${fase ? `${fase.codigo} — ${this._esc(fase.nombre)}` : "Sin fase asignada"}</div>
              ${fase ? `<div class="pr-wz-fase-sub">Mes ${fase.mesInicio}–${fase.mesFin}${mult !== 1 ? ` · IPC ×${mult.toFixed(4)}` : ""}</div>` : ""}
            </div>
            <div class="pr-wz-fase-stats">
              <span class="pr-wz-fase-pill" id="wzf-${key}-count"></span>
              <span class="pr-wz-fase-monto" id="wzf-${key}-monto">$0</span>
            </div>
          </div>
          <div class="pr-wz-fase-body">
            ${this._faseItemsHtml(key, items)}
          </div>
          <div class="pr-wz-fase-subtotal"><span>Subtotal</span><span class="pr-wz-fase-monto" id="wzf-${key}-sub">$0</span></div>
        </div>`);
    }
    cont.innerHTML = html.join("");

    cont.querySelectorAll<HTMLElement>("[data-fase-toggle]").forEach((hd) => {
      hd.addEventListener("click", () => hd.closest(".pr-wz-fase")!.classList.toggle("collapsed"));
    });
    cont.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-id]').forEach((cb) => {
      cb.addEventListener("change", () => { this._itemState.get(cb.dataset.id!)!.activo = cb.checked; this._recalc(); });
    });
    cont.querySelectorAll<HTMLInputElement>(".pr-wz-cant").forEach((inp) => {
      inp.addEventListener("input", () => { this._itemState.get(inp.dataset.id!)!.cantidad = Number(inp.value) || 0; this._recalc(); });
    });
    cont.querySelectorAll<HTMLInputElement>(".pr-wz-precio").forEach((inp) => {
      inp.addEventListener("input", () => { this._itemState.get(inp.dataset.precio!)!.precio = Number(inp.value) || 0; this._recalc(); });
    });
    cont.querySelectorAll<HTMLButtonElement>("[data-quitar]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const item = this._catalogo.find((x) => x.id === btn.dataset.quitar)!;
        void this._quitarItemPropio(item);
      });
    });

    // La lista se vuelve a dibujar al agregar o quitar un ítem propio; la búsqueda que había puesta sigue.
    this._filtrarItems();
  }

  /**
   * Filtra la lista del Paso 3 **escondiendo filas, sin volver a dibujarla**: así el input no pierde el
   * foco mientras se escribe -si se redibujara, solo quedaría la primera letra- y, lo importante, el
   * presupuesto no se toca. `_recalc` suma sobre `_catalogo`, no sobre lo que se ve, así que esconder una
   * fila nunca puede mover el total. Los montos por fase sí se esconden mientras hay búsqueda (lo hace el
   * CSS con `.filtrando`), porque un subtotal al lado de una lista filtrada se lee como el total de lo
   * filtrado, que no es.
   *
   * Un proyecto real trae 80+ ítems repartidos en fases colapsables: sin esto hay que abrirlas una por una
   * y leer a ojo. Compara con la regla común de los buscadores (`busquedaTexto.ts`, desde el 2026-10-31 también
   * acá): todas las palabras, en cualquier orden, sin tildes ni mayúsculas.
   */
  private _filtrarItems(): void {
    const cont = this._body.querySelector<HTMLElement>("#wz-items");
    if (!cont) return;

    const palabras = palabrasBusqueda(this._itemBusqueda);
    const filtrando = palabras.length > 0;
    cont.classList.toggle("filtrando", filtrando);

    const subsConMatch = new Set<string>();
    const fasesConMatch = new Set<Element>();
    let visibles = 0;
    let total = 0;

    cont.querySelectorAll<HTMLElement>(".pr-wz-itemrow").forEach((row) => {
      total++;
      const coincide = !filtrando || coincideBusqueda(palabras, row.dataset.buscar);
      row.style.display = coincide ? "" : "none";
      if (!coincide) return;
      visibles++;
      if (row.dataset.subKey) subsConMatch.add(row.dataset.subKey);
      const fase = row.closest(".pr-wz-fase");
      if (fase) fasesConMatch.add(fase);
    });

    // El rótulo de una subsección sin coincidencias sobra, y una fase entera sin coincidencias estorba.
    cont.querySelectorAll<HTMLElement>(".pr-sub-lbl").forEach((lbl) => {
      lbl.style.display = !filtrando || subsConMatch.has(lbl.dataset.subKey ?? "") ? "" : "none";
    });
    cont.querySelectorAll<HTMLElement>(".pr-wz-fase").forEach((fase) => {
      fase.style.display = !filtrando || fasesConMatch.has(fase) ? "" : "none";
    });

    const contador = this._body.querySelector<HTMLElement>("#wz-item-count");
    if (contador) contador.textContent = filtrando ? `${visibles} de ${total}` : "";
  }

  /** Tira de contexto del Paso 3 (beneficiario · finca · ha · cultivo), como `.ctx` del mockup v2. */
  private _ctxStripHtml(): string {
    const p = this._created;
    if (!p) return "";
    const finca = this._fincas.find((f) => f.id === p.fincaId)?.nombreFinca ?? p.municipio;
    const chips = [
      `<span class="pr-wz-ctx-chip"><i class="ph ph-user"></i><strong>${this._esc(p.clienteNombre)}</strong></span>`,
      finca ? `<span class="pr-wz-ctx-chip"><i class="ph ph-map-pin"></i><strong>${this._esc(finca)}</strong></span>` : "",
      `<span class="pr-wz-ctx-chip"><i class="ph ph-ruler"></i><strong>${formatKg(p.ha)} Ha</strong></span>`,
      `<span class="pr-wz-ctx-chip"><i class="ph ph-plant"></i><strong>${this._esc(p.cultivoNombre)}${p.cultivoMesesACosecha ? ` · ${p.cultivoMesesACosecha} meses` : ""}</strong></span>`,
    ].filter(Boolean);
    return `<div class="pr-wz-ctx">${chips.join('<span class="pr-wz-ctx-sep">·</span>')}</div>`;
  }

  private _faseItemsHtml(faseKey: string, items: CatalogoItemResponse[]): string {
    const html: string[] = [];
    const subs = [...new Set(items.map((it) => it.sub))];
    for (const sub of subs) {
      const subKey = `${faseKey}|${sub}`;
      html.push(`<div class="pr-sub-lbl" data-sub-key="${this._esc(subKey)}">${this._esc(sub)}</div>`);
      for (const it of items.filter((x) => x.sub === sub)) {
        const st = this._itemState.get(it.id)!;
        const esSmmlv = it.tipoP === "smmlv";
        const obligatorio = it.incl === "obligatorio";
        const precioCell = esSmmlv
          ? `<span class="pr-wz-iprecio" title="Jornal del snapshot">${st.precio > 0 ? fmtCentavos(st.precio) : "—"}</span>`
          : `<input class="sx-input sx-input--numeric pr-wz-precio" type="number" min="0" step="1" value="${st.precio || ""}" data-precio="${it.id}" placeholder="precio" title="Precio unitario (COP)">`;
        const freqTxt = it.freq > 1 ? ` · ×${it.freq}${it.freqUnidad ? " " + this._esc(it.freqUnidad) : ""}` : "";
        // Un ítem propio se marca siempre: en la fila se ve igual a uno del catálogo y hay que poder
        // distinguir de un vistazo lo que es del cultivo de lo que es solo de este beneficiario.
        const propioTag = it.esPropio
          ? `<span class="pr-tipo-badge" style="background:var(--sx-warning-bg);color:var(--sx-warning)" title="${
              it.motivo ? "Motivo: " + this._esc(it.motivo) : "Ítem propio del beneficiario"
            }${it.duenos > 1 ? ` · lo comparten ${it.duenos} beneficiarios` : ""}${
              it.creadoPorNombre ? ` · creado por ${this._esc(it.creadoPorNombre)}` : ""
            }">propio</span>`
          : "";
        // Texto del buscador. Los propios llevan además la palabra "propio" y su motivo: escribirla es
        // hoy la única forma de ver juntos los ítems que no vienen del catálogo del cultivo.
        const buscable = normalizarBusqueda(
          [it.nombre, it.categoria, sub, it.esPropio ? "propio" : "", it.motivo ?? ""].join(" "));
        html.push(`
          <div class="pr-wz-itemrow" data-id="${it.id}" data-sub-key="${this._esc(subKey)}" data-buscar="${this._esc(buscable)}">
            <label class="pr-switch" title="${obligatorio ? "Ítem obligatorio" : "Opcional"}">
              <input type="checkbox" data-id="${it.id}" ${st.activo ? "checked" : ""} ${obligatorio ? "disabled" : ""}>
              <span class="pr-switch-track"></span>
            </label>
            <div class="pr-wz-iname">
              ${obligatorio ? `<i class="ph ph-lock-simple pr-wz-lock" title="Ítem obligatorio"></i>` : ""}
              <span class="pr-cat-dot" style="background:${categoriaColor(it.categoria)}" title="${this._esc(it.categoria || "")}"></span>
              ${it.nitro ? `<span class="pr-dot" style="background:var(--sx-info);display:inline-block;margin-right:5px"></span>` : ""}${this._esc(it.nombre)} ${propioTag}
              <div class="pr-wz-icat">${this._esc(it.categoria || "")}${it.cantUnidad ? " · " + this._esc(it.cantUnidad) : ""} · ${it.tipoP}${freqTxt}${it.esPropio && it.motivo ? " · " + this._esc(it.motivo) : ""}</div>
            </div>
            ${precioCell}
            <input class="sx-input sx-input--numeric pr-wz-cant" type="number" min="0" step="0.1" value="${st.cantidad}" data-id="${it.id}" title="Cantidad por Ha">
            ${it.esPropio ? `<button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" type="button" data-quitar="${it.id}" title="Quitarle este ítem al beneficiario"><i class="ph ph-trash"></i></button>` : ""}
          </div>`);
      }
    }
    return html.join("");
  }

  private _recalc(): void {
    const ha = this._created!.ha;
    // `propios` no se suma al total: es una porción de capex+opex que se muestra aparte, para que quien
    // revisa vea cuánta plata del presupuesto viene de ítems que no están en el catálogo del cultivo.
    let capex = 0, opex = 0, activos = 0, inactivos = 0, sinPrecio = 0, propios = 0;
    for (const { key, items } of this._faseGroups()) {
      let faseSub = 0, faseActivos = 0;
      for (const it of items) {
        const st = this._itemState.get(it.id)!;
        if (!st.activo) { inactivos++; continue; }
        activos++; faseActivos++;
        if (!st.precio || st.precio <= 0) { sinPrecio++; continue; }
        const sub = st.precio * st.cantidad * ha;
        faseSub += sub;
        if (it.esPropio) propios += sub;
        if (it.tipo === "CAPEX") capex += sub; else opex += sub;
      }
      this._body.querySelector(`#wzf-${key}-count`)!.textContent = `${faseActivos} / ${items.length} ítems`;
      this._body.querySelector(`#wzf-${key}-monto`)!.textContent = fmtCentavos(faseSub);
      this._body.querySelector(`#wzf-${key}-sub`)!.textContent = fmtCentavos(faseSub);
    }
    this._body.querySelector("#wz-capex")!.textContent = fmtCentavos(capex);
    this._body.querySelector("#wz-opex")!.textContent = fmtCentavos(opex);
    this._body.querySelector("#wz-total")!.textContent = fmtCentavos(capex + opex);
    this._body.querySelector<HTMLElement>("#wz-propios-row")!.hidden = propios === 0;
    this._body.querySelector("#wz-propios")!.textContent = fmtCentavos(propios);
    this._body.querySelector("#wz-count")!.textContent = `${activos} activos · ${inactivos} inactivos`;
    const warn = this._body.querySelector<HTMLElement>("#wz-sinprecio")!;
    warn.hidden = sinPrecio === 0;
    if (sinPrecio > 0) warn.textContent = `⚠ ${sinPrecio} ítem${sinPrecio === 1 ? "" : "s"} sin precio — el presupuesto está incompleto hasta que el admin lo configure en el catálogo.`;

    const limite = this._created!.limiteFinancieroReferencial;
    const limiteBox = this._body.querySelector<HTMLElement>("#wz-limite")!;
    limiteBox.hidden = !limite;
    if (limite) {
      const total = capex + opex;
      const pct = Math.round((total / limite) * 100);
      this._body.querySelector("#wz-limite-pct")!.textContent = `${pct}%`;
      const fill = this._body.querySelector<HTMLElement>("#wz-limite-fill")!;
      const cap = this._body.querySelector<HTMLElement>("#wz-limite-cap")!;
      fill.style.width = `${Math.min(pct, 100)}%`;
      if (pct <= 80) {
        fill.style.background = "var(--sx-success)";
        cap.style.color = "var(--sx-success)";
        cap.textContent = `Dentro del límite referencial (${fmtCentavos(limite)})`;
      } else if (pct <= 100) {
        fill.style.background = "var(--sx-warning)";
        cap.style.color = "var(--sx-warning)";
        cap.textContent = `Cerca del límite referencial (${fmtCentavos(limite)})`;
      } else {
        fill.style.background = "var(--sx-error)";
        cap.style.color = "var(--sx-error)";
        cap.textContent = `Supera el límite referencial en ${fmtCentavos(total - limite)}`;
      }
    }
  }

  // ── PASO 3 (solo creación, no edición) ──────────────────────────────────────
  private _computeTotales(): { capex: number; opex: number; activos: number; inactivos: number; sinPrecio: number; propios: number; propiosCount: number } {
    const ha = this._created!.ha;
    let capex = 0, opex = 0, activos = 0, inactivos = 0, sinPrecio = 0, propios = 0, propiosCount = 0;
    for (const it of this._catalogo) {
      const st = this._itemState.get(it.id)!;
      if (!st.activo) { inactivos++; continue; }
      activos++;
      if (it.esPropio) propiosCount++;
      if (!st.precio || st.precio <= 0) { sinPrecio++; continue; }
      const sub = st.precio * st.cantidad * ha;
      if (it.esPropio) propios += sub;
      if (it.tipo === "CAPEX") capex += sub; else opex += sub;
    }
    return { capex, opex, activos, inactivos, sinPrecio, propios, propiosCount };
  }

  private _goStep4(): void {
    this._step = 4;
    this._renderStep4();
  }

  private _renderStep4(): void {
    const p = this._created!;
    const { capex, opex, activos, inactivos, sinPrecio, propios, propiosCount } = this._computeTotales();
    const total = capex + opex;
    const limite = p.limiteFinancieroReferencial;
    const superaLimite = limite != null && total > limite;

    this._body.innerHTML = `
      <div class="pr-wz-hd">
        <div class="pr-wz-title">${this._esc(p.nombre)}</div>
        <button class="pr-wz-close" id="wz-close">✕</button>
      </div>
      ${this._stepperHtml()}
      <div class="pr-form-note" style="margin-bottom:14px">Revisa la información antes de crear el proyecto. Podrás editarlo después mientras esté en Borrador.</div>

      ${limite != null ? `
        <div class="pr-alert-warn" style="margin-bottom:14px;${superaLimite ? "" : "background:var(--sx-success-bg);border-color:var(--sx-success-border);color:var(--sx-success)"}">
          <strong>${superaLimite ? "Presupuesto sobre el límite financiero de referencia" : "Presupuesto dentro del límite financiero de referencia"}</strong>
          <div style="margin-top:4px">
            Monto estimado <strong>${fmtCentavos(total)}</strong> · Límite ${fmtCentavos(limite)}
            ${superaLimite ? ` · Diferencia <strong>+${fmtCentavos(total - limite)}</strong>` : ""}
          </div>
          <div style="margin-top:4px;font-size:var(--sx-text-xs)">Esto es informativo — el sistema no bloquea la creación.</div>
        </div>` : ""}

      <div class="pr-form-row" style="margin-bottom:12px">
        <div class="pr-panel">
          <div class="pr-panel-t">Beneficiario</div>
          <div class="pr-wz-pres-row"><span>Nombre</span><span>${this._esc(p.clienteNombre)}</span></div>
          ${this._cliente ? `<div class="pr-wz-pres-row"><span>NIT</span><span>${this._esc(this._cliente.nit)}</span></div>` : ""}
          <div class="pr-wz-pres-row"><span>Ubicación</span><span>${p.municipio ? this._esc(p.municipio) : "—"}</span></div>
        </div>
        <div class="pr-panel">
          <div class="pr-panel-t">Organización</div>
          <div class="pr-wz-pres-row"><span>Macro-Proyecto</span><span>${p.macroProyectoNombre ? this._esc(p.macroProyectoNombre) : "— Ninguno —"}</span></div>
          <div class="pr-wz-pres-row"><span>Núcleo</span><span>${p.nucleoNombre ? this._esc(p.nucleoNombre) : "—"}</span></div>
          <div class="pr-wz-pres-row"><span>Técnico</span><span>${this._esc(p.tecnicoNombre)}</span></div>
        </div>
      </div>
      <div class="pr-form-row" style="margin-bottom:12px">
        <div class="pr-panel">
          <div class="pr-panel-t">Proyecto</div>
          <div class="pr-wz-pres-row"><span>Cultivo</span><span>${this._esc(p.cultivoNombre)}${p.variedad ? " " + this._esc(p.variedad) : ""}</span></div>
          <div class="pr-wz-pres-row"><span>Área</span><span>${p.ha} Ha</span></div>
          <div class="pr-wz-pres-row"><span>Inicio</span><span>${p.fechaInicio ? new Date(p.fechaInicio).toLocaleDateString("es-CO") : "— Sin definir —"}</span></div>
          <div class="pr-wz-pres-row"><span>Duración</span><span>${p.duracionMeses ? p.duracionMeses + " meses" : "— Sin definir —"}</span></div>
        </div>
        <div class="pr-panel">
          <div class="pr-panel-t">Catálogo</div>
          <div class="pr-wz-pres-row"><span>Ítems activos</span><span>${activos} / ${activos + inactivos}</span></div>
          <div class="pr-wz-pres-row"><span>CAPEX</span><span>${fmtCentavos(capex)}</span></div>
          <div class="pr-wz-pres-row"><span>OPEX anual</span><span>${fmtCentavos(opex)}</span></div>
          ${propiosCount > 0 ? `<div class="pr-wz-pres-row"><span>Ítems propios del beneficiario</span><span>${propiosCount} · ${fmtCentavos(propios)}</span></div>` : ""}
          <div class="pr-wz-pres-row"><span>Estado inicial</span><span style="color:var(--sx-warning)">Borrador</span></div>
        </div>
      </div>

      <div class="pr-panel" style="text-align:center;padding:18px">
        <div class="pr-wz-lbl">Presupuesto estimado total</div>
        <div style="font-size:29px;font-weight:var(--sx-weight-semibold);margin:4px 0">${fmtCentavos(total)}</div>
        <div class="pr-form-note">Suma de ítems activos por fase · ${p.ha} Ha${sinPrecio > 0 ? ` · ⚠ ${sinPrecio} ítem${sinPrecio === 1 ? "" : "s"} sin precio, no incluido${sinPrecio === 1 ? "" : "s"}` : ""}${propios > 0 ? ` · incluye ${fmtCentavos(propios)} de ítems propios, fuera del catálogo del cultivo` : ""}</div>
      </div>

      <div class="pr-wz-footer pr-wz-footer-nav">
        <button class="sx-btn sx-btn-ghost" id="wz-back3">← Anterior</button>
        <button class="sx-btn sx-btn-primary" id="wz-confirmar">Crear proyecto</button>
      </div>
    `;
    this.getElement().querySelector<HTMLButtonElement>("#wz-close")!.onclick = () => this.dispose();
    this.getElement().querySelector<HTMLButtonElement>("#wz-back3")!.onclick = () => { this._step = 3; this._renderStep3(); };
    this.getElement().querySelector<HTMLButtonElement>("#wz-confirmar")!.onclick = () => void this._crear();
  }

  private async _crear(): Promise<void> {
    const items: ProyectoItemPayload[] = this._catalogo.map((it) => {
      const st = this._itemState.get(it.id)!;
      // smmlv → null (el backend usa el factor jornal del snapshot); resto → precio ingresado (null si vacío).
      const precioUnitario = it.tipoP === "smmlv" ? null : (st.precio > 0 ? st.precio : null);
      return { itemId: it.id, cantidad: st.cantidad, precioUnitario, activo: st.activo };
    });
    const btns = this.getElement().querySelectorAll<HTMLButtonElement>(".pr-wz-footer button");
    btns.forEach((b) => (b.disabled = true));
    try {
      const saved = await ProyectoService.instance.saveItems(this._created!.id, { items });
      this._onCreated(saved);
      this.dispose();
    } catch (error) {
      errorMessageComponent.addError("Error al guardar el paquete", (error as Error).message);
      btns.forEach((b) => (b.disabled = false));
    }
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
